import {
    BaseInput,
    CoreFetch,
    CurrencyType,
    InvoiceApi,
    InvoiceType,
    ProviderPatientStatement,
    IToastContextProps,
} from '@enter/core';
import { mdiChevronRight, mdiTagOutline } from '@mdi/js';
import React, { useEffect, useMemo, useState } from 'react';
import { IDialogContext } from '../statement-details.types';
import { StatementApplyToClaims } from './statement-apply-to-claims';
import {
    allocateAmountAcrossClaims,
    ApplyMode,
    getOpenBalanceClaims,
    resolveTargetClaims,
    sortClaimsByOldestDos,
} from './statement-apply-to-claims.utils';
import { parseLedgerAmount } from '../utils/statement-format.utils';
import * as S from './statement-ledger-dialog.styles';

type RecordDiscountDialogProps = {
    patientStatement?: ProviderPatientStatement;
    onClose: () => void;
    onSuccess: () => void;
    dialogContext: IDialogContext;
    toastContext: IToastContextProps;
    equalsFilter: (term?: string | string[]) => string[] | undefined;
};

export const RecordDiscountDialog: React.FunctionComponent<RecordDiscountDialogProps> = (props) => {
    const { dialogContext, toastContext, equalsFilter, patientStatement, onClose, onSuccess } = props;

    const [applyMode, setApplyMode] = useState<ApplyMode>('auto');
    const [selectedClaimIds, setSelectedClaimIds] = useState<Record<string, boolean>>({});
    const [memo, setMemo] = useState<string>('Patient Loyalty Adjustment');
    const [amount, setAmount] = useState<string>('');
    const [baseAmount, setBaseAmount] = useState<string>('0');
    const [endingAmount, setEndingAmount] = useState<string>('0');
    const [showInvoiceDetails, setShowInvoiceDetails] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isLoadingInvoice, setIsLoadingInvoice] = useState(false);
    const [errors, setErrors] = useState<Record<string, string>>({});

    const openClaims = useMemo(() => getOpenBalanceClaims(patientStatement), [patientStatement]);

    const targetClaims = useMemo(
        () => resolveTargetClaims(openClaims, applyMode, selectedClaimIds),
        [applyMode, openClaims, selectedClaimIds],
    );

    const selectedBalanceTotal = targetClaims.reduce((sum, claim) => sum + claim.balance, 0);

    useEffect(() => {
        const loadInvoiceDetails = async (): Promise<void> => {
            if (targetClaims.length !== 1) {
                setBaseAmount(selectedBalanceTotal.toFixed(2));
                setEndingAmount((selectedBalanceTotal - parseLedgerAmount(amount)).toFixed(2));
                return;
            }

            const claimId = targetClaims[0].id;
            setIsLoadingInvoice(true);
            try {
                const invoices = await CoreFetch<InvoiceApi>(InvoiceApi).listProviderInvoices({
                    filterClaimId: equalsFilter(claimId),
                    filterInvoiceType: equalsFilter(InvoiceType.PATIENT),
                });

                if (!invoices || invoices.length === 0) {
                    const fallback = targetClaims[0].balance.toFixed(2);
                    setBaseAmount(fallback);
                    setEndingAmount((targetClaims[0].balance - parseLedgerAmount(amount)).toFixed(2));
                    return;
                }

                const invoice = invoices[invoices.length - 1];
                const base = invoice?.amount?.amount || '0';
                setBaseAmount(base);
                setEndingAmount((parseLedgerAmount(base) - parseLedgerAmount(amount)).toFixed(2));
            } catch (err) {
                dialogContext.showError(err);
                const fallback = targetClaims[0].balance.toFixed(2);
                setBaseAmount(fallback);
                setEndingAmount((targetClaims[0].balance - parseLedgerAmount(amount)).toFixed(2));
            } finally {
                setIsLoadingInvoice(false);
            }
        };

        void loadInvoiceDetails();
        // eslint-disable-next-line react-hooks/exhaustive-deps -- refresh when claim selection changes; amount handled separately
    }, [applyMode, selectedClaimIds, openClaims, selectedBalanceTotal]);

    const validate = (): boolean => {
        const newErrors: Record<string, string> = {};
        const parsedAmount = parseLedgerAmount(amount);

        if (!amount || parsedAmount <= 0) {
            newErrors.amount = 'Discount amount is required and must be greater than 0';
        }

        if (!targetClaims.length) {
            newErrors.claims =
                applyMode === 'auto'
                    ? 'No claims with an open balance available'
                    : 'Select at least one claim';
        }

        if (!memo || memo.trim() === '') {
            newErrors.memo = 'Discount type is required';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const postDiscountToClaim = async (claimId: string, discountAmount: number): Promise<void> => {
        const invoices = await CoreFetch<InvoiceApi>(InvoiceApi).listProviderInvoices({
            filterClaimId: equalsFilter(claimId),
            filterInvoiceType: equalsFilter(InvoiceType.PATIENT),
        });

        if (!invoices || invoices.length === 0) {
            throw new Error(`No patient invoice found for claim ${claimId}`);
        }

        const invoiceId = invoices[invoices.length - 1].id;
        const finalAmount = discountAmount > 0 ? -discountAmount : discountAmount;

        await CoreFetch<InvoiceApi>(InvoiceApi).postProviderInvoiceItem({
            id: invoiceId,
            createBillingInvoiceItemPayload: {
                name: memo.trim(),
                amount: {
                    amount: finalAmount.toString(),
                    currency: CurrencyType.USD,
                },
            },
        });
    };

    const handleSubmit = async (): Promise<void> => {
        if (!validate()) {
            return;
        }

        setIsSubmitting(true);

        try {
            const parsedAmount = parseLedgerAmount(amount);
            const orderedClaims = [...targetClaims].sort(sortClaimsByOldestDos);
            const allocations = allocateAmountAcrossClaims(orderedClaims, parsedAmount);

            if (!allocations.length) {
                throw new Error('Unable to allocate discount to selected claims');
            }

            for (const allocation of allocations) {
                await postDiscountToClaim(allocation.claimId, allocation.amount);
            }

            toastContext.notify?.(
                allocations.length > 1
                    ? `Discount was recorded across ${allocations.length} claims.`
                    : 'Discount was recorded successfully.',
                'right',
            );
            onSuccess();
        } catch (err) {
            dialogContext.showError(err);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
        const next = e.target.value;
        setAmount(next);
        const base = parseLedgerAmount(baseAmount);
        const discount = parseLedgerAmount(next);
        setEndingAmount((base - discount).toFixed(2));
    };

    const handleEndingAmountChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
        const next = e.target.value;
        setEndingAmount(next);
        const base = parseLedgerAmount(baseAmount);
        const ending = parseLedgerAmount(next);
        setAmount((base - ending).toFixed(2));
    };

    const toggleClaim = (claimId: string): void => {
        setSelectedClaimIds(prev => ({
            ...prev,
            [claimId]: !prev[claimId],
        }));
    };

    return (
        <S.DialogBody>
            <S.Header>
                <S.HeaderIcon path={mdiTagOutline} size={0.9} />
                <S.HeaderTitle>Record discount</S.HeaderTitle>
            </S.Header>

            <S.FormSection>
                <S.Label>Amount</S.Label>
                <S.AmountField>
                    <S.AmountPrefix>$</S.AmountPrefix>
                    <BaseInput
                        type="number"
                        step="0.01"
                        min="0"
                        placeholder="0.00"
                        value={amount}
                        onChange={handleAmountChange}
                        disableLatin1Validation
                    />
                </S.AmountField>
                {errors.amount && <S.ErrorText>{errors.amount}</S.ErrorText>}

                <S.CollapseToggle
                    type="button"
                    variant="unstyled"
                    onClick={() => setShowInvoiceDetails(open => !open)}>
                    <S.CollapseIcon path={mdiChevronRight} size={0.65} open={showInvoiceDetails} />
                    Invoice amount details
                    {isLoadingInvoice ? ' (loading…)' : ''}
                </S.CollapseToggle>

                {showInvoiceDetails && (
                    <S.CollapsePanel>
                        <div>
                            <S.Label>Base Invoice Amount</S.Label>
                            <BaseInput type="number" value={baseAmount} placeholder="0.00" readOnly />
                        </div>
                        <div>
                            <S.Label>Ending Invoice Amount</S.Label>
                            <BaseInput
                                type="number"
                                value={endingAmount}
                                onChange={handleEndingAmountChange}
                                placeholder="0.00"
                            />
                        </div>
                    </S.CollapsePanel>
                )}
            </S.FormSection>

            <StatementApplyToClaims
                applyMode={applyMode}
                onApplyModeChange={setApplyMode}
                openClaims={openClaims}
                selectedClaimIds={selectedClaimIds}
                onToggleClaim={toggleClaim}
                targetClaims={targetClaims}
                errorMessage={errors.claims}
            />

            <S.FormSection>
                <S.Label>Discount type</S.Label>
                <BaseInput
                    value={memo}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setMemo(e.target.value)}
                    errorMessage={errors.memo}
                    placeholder="Enter discount type"
                    required
                />
            </S.FormSection>

            <S.ButtonGroup
                changed={true}
                useNotify={false}
                onCancel={onClose}
                onSave={handleSubmit}
                saveLabel={isSubmitting ? 'Applying...' : 'Apply discount'}
                saveNotice="Discount recorded successfully"
            />
        </S.DialogBody>
    );
};
